"use client";

import { useState } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";

import { Icon } from "@/components/ui/icon";
import { useInlineNodeDecoration } from "@/features/ai-prompts/lib/use-inline-node-decoration";
import { cn } from "@/lib/utils";

import type { AiPromptBlockAttrs, AiPromptBlockOptions } from "./ai-prompt-extension";

export function AiPromptBlockView(props: NodeViewProps) {
  const { node, editor, extension, deleteNode, getPos, selected } = props;
  const attrs = node.attrs as AiPromptBlockAttrs;
  const editable = editor.isEditable;
  const label =
    attrs.label && attrs.label.trim().length > 0 ? attrs.label : "AI Prompt";

  const [hovered, setHovered] = useState(false);
  const [gearKey, setGearKey] = useState(0);

  // Same selection outline + formatting (bold/italic/underline/link) the
  // variable chip uses — prompts are inline atoms too, and the backend wraps
  // their output in the matching tags.
  const { isSelected, isBold, isItalic, isUnderline, isLink } =
    useInlineNodeDecoration({ node, editor, getPos, selected });

  // `updateAiPromptBlock` edits the selected node, so only open the editor
  // once this chip holds the node selection.
  const handleClick = () => {
    if (!editable) return;
    const pos = getPos();
    if (typeof pos !== "number") return;
    editor.commands.setNodeSelection(pos);
    (extension.options as AiPromptBlockOptions).onEditNode?.(attrs);
  };

  return (
    <NodeViewWrapper
      as="span"
      className="inline-flex items-baseline align-baseline"
    >
      <button
        type="button"
        disabled={!editable}
        onClick={handleClick}
        onMouseEnter={() => {
          setHovered(true);
          setGearKey((k) => k + 1);
        }}
        onMouseLeave={() => setHovered(false)}
        className={cn(
          "group/chip mx-0.5 inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5",
          "text-xs leading-tight whitespace-nowrap select-none",
          isBold ? "font-bold" : "font-medium",
          "transition-colors cursor-pointer",
          "disabled:cursor-default disabled:opacity-70",
          "bg-ai/[0.06] border-ai/15 text-ai",
          "hover:bg-ai/[0.12]",
          // Selection state — swap the border to blue (same as variables) so
          // it's clear the prompt is part of the current selection.
          isSelected &&
            "!border-[#2b5df3] bg-[rgba(43,93,243,0.10)] dark:bg-[rgba(43,93,243,0.16)]",
        )}
      >
        <span className="relative flex size-3 items-center justify-center">
          <span
            className={cn(
              "absolute inset-0 flex items-center justify-center transition-opacity duration-150",
              hovered ? "opacity-0" : "opacity-80",
            )}
          >
            <Icon name="IconPrompt" size={12} />
          </span>
          <span
            key={gearKey}
            className={cn(
              "absolute inset-0 flex items-center justify-center transition-opacity duration-150",
              hovered ? "opacity-80 animate-[var-gear-spin_0.4s_ease-out_forwards]" : "opacity-0",
            )}
          >
            <Icon name="IconSettingsGear2" size={12} />
          </span>
        </span>
        <span
          className={cn(
            "truncate",
            isItalic && "italic",
            (isUnderline || isLink) && "underline",
            // A link shows blue text + blue underline (underline color follows
            // text color) so it's distinct from a plain underline.
            isLink && "text-[#86b3ff]",
          )}
        >
          {label}
        </span>
        {editable && (
          <Icon
            name="IconCrossMedium"
            size={10}
            className="opacity-50 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              deleteNode();
            }}
          />
        )}
      </button>
    </NodeViewWrapper>
  );
}
